import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  Ban,
  Check,
  Copy,
  ExternalLink,
  Mail,
  Save,
} from "lucide-react";

import { adminGet, adminPatch, adminPost } from "@lib/adminApi";

import type { Gift } from "./types";

export const GiftDetail = () => {
  const { giftId } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState<Gift | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Editable fields
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [deliveryType, setDeliveryType] = useState("");

  const fetchGift = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGet<Gift>(`/admin/gifts/${giftId}`);
      setGift(data);
      setRecipientEmail(data.recipientEmail ?? "");
      setSenderName(data.senderName);
      setGiftMessage(data.message ?? "");
      setScheduledAt(data.scheduledAt ? data.scheduledAt.substring(0, 16) : "");
      setDeliveryType(data.deliveryType);
    } catch (err) {
      console.error("Failed to fetch gift:", err);
    } finally {
      setLoading(false);
    }
  }, [giftId]);

  useEffect(() => {
    fetchGift();
  }, [fetchGift]);

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminPatch<Gift>(`/admin/gifts/${giftId}`, {
        recipientEmail: recipientEmail || null,
        senderName,
        message: giftMessage || null,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        deliveryType,
      });
      setGift(updated);
      showMessage("success", "Gift updated successfully");
    } catch {
      showMessage("error", "Failed to update gift");
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    try {
      await adminPost(`/admin/gifts/${giftId}/resend`);
      await fetchGift();
      showMessage("success", "Gift email resent successfully");
    } catch {
      showMessage("error", "Failed to resend gift email");
    }
  };

  const handleCancel = async () => {
    if (
      !confirm(
        "Are you sure you want to cancel this gift? This will prevent redemption.",
      )
    )
      return;
    try {
      const updated = await adminPost<Gift>(`/admin/gifts/${giftId}/cancel`);
      setGift(updated);
      showMessage("success", "Gift cancelled");
    } catch {
      showMessage("error", "Failed to cancel gift");
    }
  };

  const handleGenerateCardLink = async () => {
    try {
      const result = await adminGet<{ url: string }>(
        `/admin/gifts/${giftId}/card-link`,
      );
      navigator.clipboard.writeText(result.url);
      showMessage("success", "Card link copied to clipboard");
    } catch {
      showMessage("error", "Failed to generate card link");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showMessage("success", "Copied to clipboard");
  };

  if (loading) {
    return <div className="p-4 text-gray-400">Loading...</div>;
  }

  if (!gift) {
    return <div className="p-4 text-red-500">Gift not found</div>;
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/gifts")}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="text-2xl font-bold">Gift #{gift.id}</h2>
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            gift.status === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : gift.status === "sent"
                ? "bg-green-100 text-green-800"
                : "bg-blue-100 text-blue-800"
          }`}
        >
          {gift.status}
        </span>
      </div>

      {/* Message banner */}
      {message && (
        <div
          className={`px-4 py-2 rounded text-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Read-only info */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-semibold mb-3 text-gray-700">Gift Details</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Gift Code</span>
            <div className="flex items-center gap-2 mt-1">
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                {gift.code}
              </code>
              <button onClick={() => copyToClipboard(gift.code)}>
                <Copy className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
          </div>
          <div>
            <span className="text-gray-500">Transaction ID</span>
            <div className="mt-1 font-mono text-xs">{gift.transactionId}</div>
          </div>
          <div>
            <span className="text-gray-500">Product</span>
            <div className="mt-1">{gift.productDisplayName}</div>
          </div>
          <div>
            <span className="text-gray-500">Purchaser</span>
            <div className="mt-1">{gift.purchaserEmail}</div>
          </div>
        </div>
      </div>

      {/* Editable fields */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-semibold mb-3 text-gray-700">Editable Fields</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-gray-500 block mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              className="border rounded px-3 py-2 w-full text-sm"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">
              Sender Name
            </label>
            <input
              type="text"
              className="border rounded px-3 py-2 w-full text-sm"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">
              Gift Message
            </label>
            <textarea
              className="border rounded px-3 py-2 w-full text-sm"
              rows={3}
              value={giftMessage}
              onChange={(e) => setGiftMessage(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-500 block mb-1">
                Delivery Type
              </label>
              <select
                className="border rounded px-3 py-2 w-full text-sm"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
              >
                <option value="send">Email</option>
                <option value="print">Print</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">
                Scheduled Delivery
              </label>
              <input
                type="datetime-local"
                className="border rounded px-3 py-2 w-full text-sm"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-semibold mb-3 text-gray-700">Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button
            className="flex items-center gap-2 border px-3 py-2 rounded text-sm hover:bg-gray-50"
            onClick={handleResend}
          >
            <Mail className="h-4 w-4" />
            Resend Gift Email
          </button>
          <button
            className="flex items-center gap-2 border px-3 py-2 rounded text-sm hover:bg-gray-50"
            onClick={handleGenerateCardLink}
          >
            <ExternalLink className="h-4 w-4" />
            Copy Printable Card Link
          </button>
          <button
            className="flex items-center gap-2 border px-3 py-2 rounded text-sm hover:bg-gray-50"
            onClick={() => copyToClipboard(gift.code)}
          >
            <Copy className="h-4 w-4" />
            Copy Gift Code
          </button>
          {gift.status === "pending" && (
            <button
              className="flex items-center gap-2 border border-green-300 text-green-700 px-3 py-2 rounded text-sm hover:bg-green-50"
              onClick={handleResend}
            >
              <Check className="h-4 w-4" />
              Mark as Sent
            </button>
          )}
          {/* Sent gifts can still be cancelled: refund requests usually arrive after delivery (OMG-2708) */}
          {gift.status !== "used" && (
            <button
              className="flex items-center gap-2 border border-red-300 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-50"
              onClick={handleCancel}
            >
              <Ban className="h-4 w-4" />
              Cancel Gift
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
